/* Verbatim port of renderProfiles, setViewMode, FAMILY_DEFS, renderPGroups
 * from service/review/index.html (~2629-2749) — §4 Websites & profiles:
 * the matrix/map view-toggle shell + platform-family drill-down groups.
 * The matrix table itself (renderMatrix/matrixCellFor) is views/matrix.ts;
 * the SVG mind-map/graph engine (initGraph, ...) is ui/graph.ts.
 *
 * PARAMETERIZED (rule a): renderProfiles/renderPGroups read the global
 * STATE — take `state: AppPayloadState` as an explicit first parameter.
 *
 * DEVIATION (dead local): the original computed `const unclaimed =
 * STATE.assets.filter(...).length` (~2634) but never read it anywhere in
 * renderProfiles' body (only the same-named, differently-scoped local
 * inside renderPGroups, ~2731, is actually used). Dropped here under TS
 * `noUnusedLocals` — same treatment as the dead `others`/`terms` locals
 * documented in ui/popover.ts and views/timeline.ts.
 *
 * DEVIATION (module state): `VIEW_MODE` (originally ~2629, `let VIEW_MODE
 * = 'matrix'`) stays module-local, same TL_STATE-style precedent as
 * views/timeline.ts/search.ts — only this module's own setViewMode() ever
 * reassigns it, so there's no cross-module read-only-import-binding
 * problem to route around.
 *
 * F3 (wave B): innerHTML → lit render(); chip() → chipT(). The Matrix/Map
 * toggle buttons' `onclick="setViewMode('...')"` (main.ts's window-shim
 * entry, id-only) becomes `@click=${() => setViewMode('matrix'|'map',
 * state)}` calling this module's own exported setViewMode directly with
 * its real 2-arg signature — this was the window shim's ONLY consumer
 * (grepped repo-wide), so main.ts's `setViewMode` entry is removed (see
 * the F3b report's checklist), same treatment F3a gave entity.ts's
 * exportQuickStatements/exportSameAs.
 *
 * GRAPH INTERPLAY (brief's special note): `<svg id="map">` and its
 * sibling containers (#mapLegend, #mapSummary, #matrixTbl, #pgroups) carry
 * NO dynamic bindings in this file's own template — lit therefore treats
 * each as fully static markup and never touches its children on a
 * re-render (lit only diffs `${}` binding positions; a childless static
 * element it created once is left alone on every subsequent render() call
 * against the same template shape). ui/graph.ts's own imperative
 * `svg.innerHTML = ...` (drawGraph) and this file's own `renderMatrix`/
 * `renderPGroups` sub-renders are UNCONDITIONALLY re-invoked at the end of
 * every renderProfiles() call when applicable (renderMatrix/renderPGroups
 * always; initGraph only when VIEW_MODE==='map', matching the original's
 * own unconditional-rebuild-then-conditional-initGraph control flow
 * exactly) — so those containers are always freshly repainted regardless
 * of whether lit reused or recreated the DOM node hosting them. No
 * orphaning: verified via e2e/sections.spec.ts's "§4 Matrix/Map view
 * toggle" test (round-trips matrix→map→matrix) after this conversion.
 */
import { html, nothing, render } from 'lit';
import { footprint } from '../derive/exec.ts';
import { matrixEntities } from '../derive/entity.ts';
import { BAD_STATUS, CAT_LABEL } from '../derive/axes.ts';
import { chipT } from '../lib/templates.ts';
import { domainOf, faviconUrl } from '../lib/format.ts';
import { openDrawer } from '../ui/drawer.ts';
import { initGraph } from '../ui/graph.ts';
import { renderMatrix } from './matrix.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import type { NormalizedAsset } from '../types/contract.ts';

export type ProfilesViewMode = 'matrix' | 'map';
let VIEW_MODE: ProfilesViewMode = 'matrix';

export function renderProfiles(state: AppPayloadState): void {
  const el = document.getElementById('profilesBody')!;
  const fp = footprint(state);
  const ents = matrixEntities(state);
  const stale = state.assets.filter((a) => BAD_STATUS.includes(a.status)).length;

  render(html`
    <div class="finding">${state.brand}'s footprint spans <b>${fp.total} mapped surfaces</b> across ${ents.length} entities — the client directly controls ${fp.controlled}, while <b>${fp.claimable} third-party listings are claimable</b>${stale ? ` and ${stale} surface${stale>1?'s are':' is'} flagged stale or abandoned` : ''}.</div>
    <div class="why"><b>Why this matters:</b> a strong profile on every platform that matters — claimed, complete, and cross-linked — is what makes the entity read as cohesive to Google and to AI engines. The matrix below is the working surface: one row per entity, one column per platform; click any cell for the full record. <b>Map view</b> shows the same footprint as a linked network — which profiles are verifiably connected to the core site, and which float unanchored.</div>

    <div class="axisbar">
      <span class="seg"><button class="${VIEW_MODE==='matrix'?'on':''}" @click=${() => setViewMode('matrix', state)}>Matrix</button><button class="${VIEW_MODE==='map'?'on':''}" @click=${() => setViewMode('map', state)}>Map view</button></span>
      <span class="axislegend">
        <span><i style="background:var(--own-c)"></i>Owned</span>
        <span><i style="background:var(--edit-c)"></i>Editable (client)</span>
        <span><i style="background:var(--claim-c)"></i>Claimable</span>
        <span><i style="background:var(--unctrl-c)"></i>Not controlled</span>
      </span>
    </div>

    <div id="matrixView" class="${VIEW_MODE==='matrix'?'':'hidden'}">
      <div class="matrixwrap" style="margin-bottom:18px"><table class="matrix" id="matrixTbl"></table></div>
      <h3 style="font-size:16px;margin:22px 0 4px">Every surface, grouped by platform family</h3>
      <div class="sub" style="font-size:12px;color:var(--muted);margin-bottom:12px">expand a family to see each profile — click a card for its full record</div>
      <div id="pgroups"></div>
    </div>

    <div id="mapView" class="${VIEW_MODE==='map'?'':'hidden'}">
      <div id="mapwrap">
        <svg id="map"></svg>
        <div class="maplegend" id="mapLegend"></div>
        <div class="maphint">ctrl+scroll to zoom · drag to pan · click a node for detail</div>
        <div class="zoomctl"><button id="zin">+</button><button id="zout">−</button><button id="zfit" title="reset">⌂</button></div>
      </div>
      <div class="caption" id="mapSummary"></div>
    </div>`, el);

  renderMatrix(state);
  renderPGroups(state);
  if (VIEW_MODE === 'map') requestAnimationFrame(() => initGraph(state));
}
export function setViewMode(m: ProfilesViewMode, state: AppPayloadState): void { VIEW_MODE = m; renderProfiles(state); }

const FAMILY_DEFS: Array<[string, (a: NormalizedAsset) => boolean]> = [
  ['Owned websites & apps', a => a.category === 'owned'],
  ['Social profiles', a => (a.assetType === 'social-profile' || a.assetType === 'video-channel') && a.category !== 'owned'],
  ['Review & employer platforms', a => a.assetType === 'review-site' || a.assetType === 'careers-profile'],
  ['Financial & company data', a => a.assetType === 'financial-profile' || a.assetType === 'regulatory-listing'],
  ['Directories & listings', a => a.assetType === 'directory-listing' || a.assetType === 'app'],
  ['Wikis & reference', a => a.assetType === 'wiki'],
  ['Forums & community', a => a.assetType === 'forum'],
  ['News & media coverage', a => a.assetType === 'news-coverage'],
  ['Other', () => true],
];
function renderPGroups(state: AppPayloadState): void {
  const el = document.getElementById('pgroups')!;
  const used = new Set<string>();
  const groups = FAMILY_DEFS.map(([label, test]) => {
    const items = state.assets.filter((a) => !used.has(a.id) && test(a));
    items.forEach((a) => used.add(a.id));
    if (!items.length) return nothing;
    const iffy = items.filter((a) => BAD_STATUS.includes(a.status) || a.sentiment === 'negative').length;
    const unclaimed = items.filter((a) => a.status === 'unclaimed').length;
    // R12 red re-budget: §4 accordion pills are ghost counts; red only for a
    // count that CONTAINS an Undesirable (sentiment-negative). Split the old
    // single "stale or negative" red chip: negatives → red, stale-only → ghost.
    const negN = items.filter((a) => a.sentiment === 'negative').length;
    const staleN = iffy - negN;
    return html`<details class="pgroup">
      <summary><span class="arrow">▶</span>${label}<span class="cnt">${items.length}</span>
        <span class="flags">${unclaimed ? html`<span class="pill-meta count">${unclaimed} unclaimed</span>` : nothing}${staleN ? html`<span class="pill-meta count">${staleN} stale</span>` : nothing}${negN ? chipT(negN + ' negative','bad') : nothing}</span></summary>
      <div class="pbody">${items.map((a) => html`
        <div class="profcard c-${a.category}" data-id="${a.id}" @click=${() => openDrawer(state, a.id)}>
          <div class="pt">${a.url ? html`<img src="${faviconUrl(a.url,64)}" alt="">` : nothing}
            <div style="min-width:0;flex:1"><div class="n">${a.title || a.platform}</div>
            <div class="u">${domainOf(a.url) || ''}${a.region && a.region !== 'global' ? ' · ' + a.region : ''}</div></div></div>
          <div class="pbadges">
            ${chipT(CAT_LABEL[a.category], a.category)}
            ${chipT(a.status, BAD_STATUS.includes(a.status) ? 'bad' : a.status === 'active' ? 'ok' : '')}
            ${a.sentiment && a.sentiment !== 'neutral' ? chipT(a.sentiment, a.sentiment === 'negative' ? 'bad' : a.sentiment === 'positive' ? 'ok' : 'warn') : nothing}
          </div>
        </div>`)}</div>
    </details>`;
  });
  render(html`${groups}`, el);
}
